import type { VisualType, VisualData } from '@/pages/iq-test/components/IqVisualRenderer';
import { iqPatternQuestions } from '@/mocks/iqPatternQuestions';
import { iqNumberQuestions } from '@/mocks/iqNumberQuestions';
import { iqLogicQuestions } from '@/mocks/iqLogicQuestions';
import { iqVerbalQuestions } from '@/mocks/iqVerbalQuestions';

// IQ 테스트 문항 데이터 (웩슬러 방식 평균 100, 표준편차 15 기준)
// 총 100문항. 문제 내용은 그대로 두고, 실제 문제 유형 기준으로 6개 영역으로 세분화하여
// 각 영역 안에서 난이도가 점차 어려워지는 순서(쉬움 → 어려움)로 재배치한다.

export type IqCategory =
  | 'shape-pattern'
  | 'number'
  | 'verbal'
  | 'overlap-2d'
  | 'shape-rule'
  | 'solid-3d';

export interface IqQuestion {
  id: number;
  category: IqCategory;
  question: string;
  options: string[];
  answer: number;
  explanation: string;
  visualType?: VisualType;
  visualData?: VisualData;
  optionVisualType?: VisualType;
  optionVisuals?: VisualData[];
}

export interface IqCategoryMeta {
  id: IqCategory;
  label: string;
  icon: string;
  description: string;
}

export const iqCategoryMeta: IqCategoryMeta[] = [
  {
    id: 'shape-pattern',
    label: '도형 패턴',
    icon: 'ri-shape-2-line',
    description: '기본 도형 인지, 시퀀스, 매트릭스 배열 속 도형의 순환·이동·회전',
  },
  {
    id: 'number',
    label: '숫자 수열',
    icon: 'ri-function-line',
    description: '매트릭스 배열의 덧셈·곱셈·제곱 등 산술 규칙과 수열 추론',
  },
  {
    id: 'verbal',
    label: '언어·방향',
    icon: 'ri-keyboard-box-line',
    description: '알파벳 순서·문자의 순환 규칙과 방향·화살표 배열 추론',
  },
  {
    id: 'overlap-2d',
    label: '도형 겹치기',
    icon: 'ri-contrast-2-line',
    description: '2D 블록 도형의 합집합·차집합·교집합·XOR 및 회전 융합 규칙',
  },
  {
    id: 'shape-rule',
    label: '도형 규칙·선',
    icon: 'ri-shuffle-line',
    description: '프레임과 선의 회전·조합·차집합 등 도형 내부 규칙 추론',
  },
  {
    id: 'solid-3d',
    label: '3D 입체',
    icon: 'ri-box-3-line',
    description: '3차원 입체 기둥의 겹침·합집합·차집합·교집합 추론',
  },
];

// 원본 문제 목록 (배열 순서 무관 — id로 참조)
const allQuestions: IqQuestion[] = [
  ...iqPatternQuestions,
  ...iqNumberQuestions,
  ...iqLogicQuestions,
  ...iqVerbalQuestions,
];

// 각 문항의 실제 문제 유형 매핑 (id → 세분화된 영역)
const categoryByQuestion: Record<number, IqCategory> = {
  // 도형 패턴 (12)
  1: 'shape-pattern', 2: 'shape-pattern', 3: 'shape-pattern', 4: 'shape-pattern',
  5: 'shape-pattern', 6: 'shape-pattern', 7: 'shape-pattern', 8: 'shape-pattern',
  9: 'shape-pattern', 10: 'shape-pattern', 11: 'shape-pattern', 13: 'shape-pattern',
  // 숫자 수열 (28)
  41: 'number', 42: 'number', 43: 'number', 44: 'number', 45: 'number',
  46: 'number', 47: 'number', 48: 'number', 49: 'number', 50: 'number',
  51: 'number', 52: 'number', 53: 'number', 54: 'number', 55: 'number',
  56: 'number', 57: 'number', 58: 'number', 59: 'number', 60: 'number',
  61: 'number', 62: 'number', 63: 'number', 64: 'number', 71: 'number',
  72: 'number', 73: 'number', 89: 'number',
  // 언어·방향 (7)
  65: 'verbal', 66: 'verbal', 67: 'verbal', 68: 'verbal',
  12: 'verbal', 69: 'verbal', 70: 'verbal',
  // 도형 겹치기 2D (18)
  14: 'overlap-2d', 15: 'overlap-2d', 16: 'overlap-2d', 17: 'overlap-2d',
  18: 'overlap-2d', 19: 'overlap-2d', 20: 'overlap-2d', 21: 'overlap-2d',
  33: 'overlap-2d', 34: 'overlap-2d', 35: 'overlap-2d', 38: 'overlap-2d',
  39: 'overlap-2d', 86: 'overlap-2d', 88: 'overlap-2d', 90: 'overlap-2d',
  97: 'overlap-2d', 99: 'overlap-2d',
  // 도형 규칙·선 (24)
  36: 'shape-rule', 37: 'shape-rule', 40: 'shape-rule',
  74: 'shape-rule', 75: 'shape-rule', 76: 'shape-rule', 77: 'shape-rule',
  78: 'shape-rule', 79: 'shape-rule', 80: 'shape-rule',
  81: 'shape-rule', 82: 'shape-rule', 83: 'shape-rule', 84: 'shape-rule',
  85: 'shape-rule', 87: 'shape-rule',
  91: 'shape-rule', 92: 'shape-rule',
  93: 'shape-rule', 94: 'shape-rule', 95: 'shape-rule', 96: 'shape-rule',
  98: 'shape-rule', 100: 'shape-rule',
  // 3D 입체 겹치기 (11)
  22: 'solid-3d', 23: 'solid-3d', 24: 'solid-3d', 25: 'solid-3d',
  26: 'solid-3d', 27: 'solid-3d', 28: 'solid-3d', 29: 'solid-3d',
  30: 'solid-3d', 31: 'solid-3d', 32: 'solid-3d',
};

// 유형별 + 난이도 오름차순(쉬움 → 어려움) 배치 순서
// 유형 간 순서: 도형 패턴 → 숫자 수열 → 알파벳·언어 → 방향·화살표 → 도형 겹치기 2D → 3D 입체
const orderedQuestionIds: number[] = [
  // 도형 패턴 (12)
  1, 2, 5, 4, 3, 8, 10, 6, 9, 7, 11, 13,
  // 숫자 수열 (28)
  49, 51, 50, 52, 57, 58, 55, 53, 54, 41, 42, 43, 89, 56, 71, 73, 72, 44, 64, 63, 60, 61, 62, 47, 48, 45, 46, 59,
  // 알파벳·언어 (4)
  65, 66, 67, 68,
  // 방향·화살표 (3)
  69, 70, 12,
  // 도형 겹치기 2D (42)
  17, 21, 91, 15, 19, 92, 78, 100, 18, 82, 84, 87, 36, 76, 79, 14, 16, 20, 81, 83, 85, 74, 75, 77, 80, 98, 86, 90, 97, 40, 38, 88, 99, 37, 93, 95, 96, 94, 33, 34, 35, 39,
  // 3D 입체 겹치기 (11)
  25, 29, 32, 26, 23, 27, 31, 22, 24, 28, 30,
];

export const iqQuestions: IqQuestion[] = orderedQuestionIds.map((id) => {
  const q = allQuestions.find((qq) => qq.id === id);
  if (!q) {
    throw new Error(`IQ 문항 id ${id}를 찾을 수 없습니다.`);
  }
  return { ...q, category: categoryByQuestion[id] ?? q.category };
});